import React from "react";
import { PageData } from "./useFinancialData";
import AgGridTable from "../../shared/components/AgGridTable";
import "./DashboardFinancialAgGrid.scss";
import gettext from "../../shared/utils/gettext";
import { BalanceAggregateType } from "./DashboardFinancial";

export default function DashboardFinancialAgGrid({
  page,
  aggregate,
}: {
  page: PageData;
  aggregate: BalanceAggregateType;
}) {
  const headers = page.data[0].data.header || [];
  const rows = page.data[0].data.rows;
  const tableData = rows
    ?.map((val) => {
      return val.map((row, i) => {
        return {
          [headers[i] || ""]: typeof row === "number" ? Math.round(row) : row,
        };
      });
    })
    .map((row) => Object.assign({}, ...row));
  tableData.pop();
  // headers is an array of translated headers:
  // ["Naam", "Jaar", "Gewas", "Oppervlakte",                           --> the general headers
  //  "Saldo", "Opbrengst", "Kosten",                                   --> totals
  //  "Oogst", "Yet to harvest", "Field specific revenues",             --> revenues
  //  "Uitgangsmateriaal", "GBM", "Mest", "Irrigatie", "Mechanisatie",  --> costs
  //    "Contract work", "Arbeid", "Energie", "Veldspecifieke kosten",
  //    "Overige toegerekende kosten"]
  const HEADER_INDEX_NAME = 0;
  const HEADER_INDEX_YEAR = 1;
  const HEADER_INDEX_CROP = 2;
  const HEADER_INDEX_AREA = 3;
  const HEADER_INDEX_BALANCE = 4;
  const HEADER_INDEX_REVENUE = 5;
  const HEADER_INDEX_COST = 6;
  const HEADER_INDEX_REVENUE_HARVEST = 7;
  const HEADER_INDEX_DETAIL_FIRST = HEADER_INDEX_REVENUE_HARVEST;
  const HEADER_INDEX_REVENUE_EXPECTED_HARVEST = 8;
  const HEADER_INDEX_REVENUE_CROPFIELD = 9;
  const HEADER_INDEX_COST_DETAIL_FIRST = 10;
  const HEADER_INDEXES_TOTALS = [HEADER_INDEX_BALANCE, HEADER_INDEX_REVENUE, HEADER_INDEX_COST];
  const columnDefs = headers.map((header, i) => {
    switch (true) {
      case i === HEADER_INDEX_NAME:
        return {
          headerName: header,
          field: header,
          cellClass: ["link-cell"],
          headerClass: ["bg-smoke", "border-smoke"],
        };
      case i === HEADER_INDEX_YEAR:
        return {
          headerName: header,
          field: header,
          hide: true,
        };
      case i === HEADER_INDEX_CROP:
        return {
          headerName: header,
          field: header,
          hide: true,
          headerClass: ["bg-smoke", "border-smoke"],
        };
      case i === HEADER_INDEX_AREA:
        return {
          headerName: header,
          field: header,
          filter: "agNumberColumnFilter",
          cellRenderer: "roundedAreaRenderer",
          cellStyle: { "text-align": "right" },
          headerClass: ["bg-smoke", "border-smoke", "align-right"],
        };
      case HEADER_INDEXES_TOTALS.includes(i):
        return {
          headerName: header,
          field: header,
          filter: "agNumberColumnFilter",
          cellRenderer: "roundedCurrencyRenderer",
          cellClass: ["cost-cell", "text-right"],
          headerClass: ["align-right", "bg-green--100", "border-green--100"],
        };
      case i >= HEADER_INDEX_DETAIL_FIRST:
        return {
          headerName: header,
          field: header,
          filter: "agNumberColumnFilter",
          cellRenderer: "roundedCurrencyRenderer",
          cellStyle: { "text-align": "right" },
          hide: true,
          headerClass: "align-right",
        };
      default:
        throw new Error("unhandled header type"); // should not happen
    }
  });
  const props = {
    table_id: "financial",
    columnDefs: [
      columnDefs[HEADER_INDEX_NAME],
      columnDefs[HEADER_INDEX_CROP],
      {
        headerName: gettext("Totals"),
        children: [
          columnDefs[HEADER_INDEX_AREA],
          columnDefs[HEADER_INDEX_BALANCE],
          columnDefs[HEADER_INDEX_REVENUE],
          columnDefs[HEADER_INDEX_COST],
        ],
      },
      {
        headerName: gettext("Revenues"),
        children: [
          columnDefs[HEADER_INDEX_REVENUE_HARVEST],
          columnDefs[HEADER_INDEX_REVENUE_EXPECTED_HARVEST],
          columnDefs[HEADER_INDEX_REVENUE_CROPFIELD],
        ],
      },
      {
        headerName: gettext("Detailcosts"),
        children: columnDefs.slice(HEADER_INDEX_COST_DETAIL_FIRST),
      },
    ],
    rowData: tableData,
    hideSearch: true,
    defaultColDef: {
      checkboxSelection: false,
      sortable: true,
      resizable: true,
    },
    enableRangeSelection: true,
    enableCharts: true,
    autoSizeAllColumns: true,
    onCellClicked: (e: any) => {
      if (e.column.colId === headers[0]) {
        window.location.href = `#financial/${aggregate}/${
          tableData?.findIndex((row: any) => row === e.data) + 1
        }/`;
      }
    },
    createChartContainer: true,
    chartColumns: [
      columnDefs[HEADER_INDEX_NAME].field,
      columnDefs[HEADER_INDEX_BALANCE].field,
      columnDefs[HEADER_INDEX_REVENUE].field,
      columnDefs[HEADER_INDEX_COST].field,
    ],
    getChartToolbarItems: () => [],
  };
  return (
    <div className="row">
      <div className="col-12 bg-black--10 px-2">
        <AgGridTable {...props} />
      </div>
    </div>
  );
}
